"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Info, X } from "lucide-react";

interface AprDeEmphasisTooltipProps {
  /** Text rendered next to the info icon (i.e. the "Total" label). */
  label: ReactNode;
  /** Tooltip body, e.g. "Total includes 40.04% APR". */
  content: ReactNode;
  /** Fired when the user opens the tooltip (used for click tracking). */
  onOpen?: () => void;
}

/**
 * Compact contextual tooltip used by the APR de-emphasis experiment.
 * Sits next to the "Total" label with a small ⓘ trigger; on click,
 * shows a black bubble above the trigger with a downward caret.
 */
export default function AprDeEmphasisTooltip({
  label,
  content,
  onOpen,
}: AprDeEmphasisTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsOpen((prev) => {
        if (!prev) onOpen?.();
        return !prev;
      });
    },
    [onOpen],
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current && wrapperRef.current.contains(target)) return;
      close();
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isOpen, close]);

  return (
    <span ref={wrapperRef} className="relative inline-flex items-center gap-1">
      <span>{label}</span>
      <button
        type="button"
        onClick={toggle}
        aria-label="More information"
        aria-expanded={isOpen}
        className="inline-flex"
      >
        <Info className="w-4 h-4 text-[#7E82AD]" />
      </button>
      {isOpen && (
        <span
          role="tooltip"
          className="absolute bottom-full ltr:left-8 ltr:bottom-4 rtl:right-10 rtl:bottom-4 mb-2 z-30 inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-[#16181D] px-3 py-2 text-sm font-normal text-white shadow-lg"
        >
          <span>{content}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            aria-label="Close"
            className="inline-flex"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <span
            aria-hidden="true"
            className="absolute top-full ltr:left-3 rtl:right-3 -mt-px h-0 w-0 border-x-[6px] border-t-[6px] border-x-transparent border-t-[#16181D]"
          />
        </span>
      )}
    </span>
  );
}
